import React, { useEffect, useState, useRef, useCallback } from "react";
import { uploadVideo, getStatus, getComscore, getPresignedUrl } from "../Apis/endpoints"; // Assuming these API functions are correctly defined
import { GoTrash } from "react-icons/go";
import Processesimg from "../Components/Imagessvg/processimg"; // Assuming this component exists
import { FiUpload } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Completedpop from "./completed-popup";
import ProgressBar from "./ProgressBar";
import AudioProgressBar from "./AudioProgressBar";
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { RingLoader } from 'react-spinners';

const UploadPopup = ({ onClose, popupstatus, Mode, videodata, videotype }) => {
  const ffmpeg = useRef(new FFmpeg());
  const [ffmpegLoaded, setFFmpegLoaded] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [status, setStatus] = useState(popupstatus);
  const [mode, setMode] = useState(Mode); // 'audio', 'fm', or 'bm'
  const [showCompletedPop, setShowCompletedPop] = useState(true);
  const [completedVideoId, setCompletedVideoId] = useState(null);
  const [comscore, setComscore] = useState([]);
  const [video_type, Setvideo_type] = useState('uploaded'); // 'uploaded' or 'self_recorded'
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedVideoId, setUploadedVideoId] = useState(null);

  const uploadTriggered = useRef(false);

  useEffect(() => {
    const loadFFmpeg = async () => {
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.2/dist/umd';
      ffmpeg.current.on('log', ({ message }) => console.log(message));
      ffmpeg.current.on('progress', ({ progress, time }) => {
      });

      try {
        await ffmpeg.current.load({
          coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
          wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        });
        setFFmpegLoaded(true);
      } catch (error) {
        console.error("Failed to load FFmpeg in UploadPopup:", error);
      }
    };

    loadFFmpeg();
  }, []);

  useEffect(() => {
    if (popupstatus === "submit" && videodata) {
      const fileType = Mode === 'audio' ? 'audio/mpeg' : 'video/mp4';
      const fileName = Mode === 'audio' ? "recorded-audio.mp3" : "recorded-video.mp4";

      const recordedFile = new File([videodata], fileName, { type: fileType });
      setSelectedFiles([recordedFile]);
      Setvideo_type(videotype); // Set video type 

      console.log('Data from recording:', videotype, videodata, Mode, popupstatus);
    }
  }, [popupstatus, videodata, Mode, videotype]);


  useEffect(() => {
    if (popupstatus === "submit" && selectedFiles.length > 0 && !uploadTriggered.current) {
      uploadTriggered.current = true;
      console.log("Selected files updated, initiating upload (single trigger):", selectedFiles);
      handleUpload();
    }
  }, [selectedFiles, popupstatus]);

  /**
   * Converts an audio file to MP3 format using FFmpeg.
   * @param {File} file - The input audio file.
   * @returns {Promise<File>} A promise that resolves to the converted MP3 File object.
   */
  const processAudioToMp3 = useCallback(async (file) => {
    if (!ffmpegLoaded) {
      console.error("FFmpeg not loaded, cannot process audio.");
      throw new Error("FFmpeg not loaded.");
    }

    const inputName = 'input_audio';
    const outputName = 'output.mp3';

    // Write the input file to FFmpeg's virtual file system
    await ffmpeg.current.writeFile(inputName, await fetchFile(file));

    // Execute FFmpeg command to convert audio to mp3
    // -i: input file
    // -vn: disable video recording
    // -acodec libmp3lame: specify MP3 encoder
    // -b:a 128k: audio bitrate (optional, can be adjusted)
    await ffmpeg.current.exec([
      '-i', inputName,
      '-vn',
      '-acodec', 'libmp3lame',
      '-b:a', '128k', // Set audio bitrate to 128kbps for MP3
      outputName
    ]);

    // Read the processed MP3 data
    const data = await ffmpeg.current.readFile(outputName);
    // Create a new File object with the MP3 blob and correct MIME type
    return new File([data], `${file.name.split('.')[0]}.mp3`, { type: 'audio/mpeg' });
  }, [ffmpegLoaded]); // Depend on ffmpegLoaded

  /**
   * Handles file selection from the input.
   * Performs validation and audio conversion if necessary.
   * @param {Event} event - The file input change event.
   */
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    let allowedTypes;
    let maxDuration;
    let processedFile = file; // Initialize with the original file

    if (Mode === 'audio') {
      // Allow various audio types for selection, but we'll convert to MP3 internally
      allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg'];
      maxDuration = 121; // 2 minutes for audio
    } else {
      allowedTypes = ['video/mp4'];
      maxDuration = 121; // 2 minutes for video
    }

    // Check if the file is a valid type
    if (!allowedTypes.includes(file.type) && !file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
      alert(`Only ${allowedTypes.join(', ')} files are allowed for ${Mode} mode!`);
      event.target.value = ""; // Clear the input
      return;
    }

    // Check file size (200MB = 200 * 1024 * 1024 bytes)
    const maxSize = 200 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("File size exceeds 200MB limit!");
      event.target.value = "";
      return;
    }

    // Check duration for both audio and video
    const media = document.createElement(Mode === 'audio' ? "audio" : "video");
    media.preload = "metadata";

    media.onloadedmetadata = async () => {
      const duration = media.duration; // Duration in seconds
      if (duration > maxDuration) {
        alert(`File duration exceeds ${Math.floor(maxDuration / 60)} minute limit!`);
        event.target.value = "";
        return;
      }

      // If it's an audio file and FFmpeg is loaded, process it to MP3
      if (Mode === 'audio' && ffmpegLoaded) {
        try {
          processedFile = await processAudioToMp3(file);
          console.log("Audio converted to MP3:", processedFile);
        } catch (error) {
          console.error("Error converting audio to MP3:", error);
          alert("Failed to process audio file. Please try again.");
          event.target.value = "";
          return;
        }
      }

      // If all checks pass and processing is done (or not needed), set the file
      setSelectedFiles([processedFile]);
      event.target.value = ""; // Clear the input
    };

    media.onerror = () => {
      alert("Error loading file metadata!");
      event.target.value = "";
    };

    media.src = URL.createObjectURL(file);
  };

  /**
   * Handles deleting a selected file from the list.
   * @param {number} index - The index of the file to delete.
   */
  const handleDeleteFile = (index) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
  };

  /**
   * Handles the upload process for selected files.
   * This involves getting a pre-signed URL, uploading to S3,
   * and then notifying your backend API.
   */
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    console.log("Initiating upload for files:", selectedFiles);

    try {
      const uploadedVideos = [];

      for (const file of selectedFiles) {
        console.log(`Processing file for upload: ${file.name}, Type: ${file.type}`);

        // Step 1: Get the pre-signed URL from your backend
        // Pass the mode to getPresignedUrl to ensure correct S3 path/prefix
        const presignedData = await getPresignedUrl(Mode, file.type); // Pass file.type for backend to determine extension
        console.log("Pre-signed URL data:", presignedData);

        if (!presignedData?.url || !presignedData?.file_name) {
          console.error("Failed to get pre-signed URL for", file.name);
          alert(`Failed to prepare upload for ${file.name}.`);
          continue;
        }
        console.log("Pre-signed URL received:", presignedData.url);

        // Extract `video_id` from `file_name` (e.g., from 'path/to/video_id.mp4')
        const match = presignedData.file_name.match(/\/([^/]+)\.(mp4|mp3|wav|ogg)$/);
        const videoId = match ? match[1] : null;
        if (!videoId) {
          console.error(`Failed to extract video_id from file name: ${presignedData.file_name}`);
          alert(`Failed to identify video ID for ${file.name}.`);
          continue;
        }
        console.log("Extracted video_id:", videoId);

        // Step 2: Upload the file to S3 using the pre-signed URL
        console.log(`Uploading ${file.name} to S3...`);
        const uploadResponse = await fetch(presignedData.url, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type, // Use the actual file type (which is now audio/mpeg for audio)
          },
        });

        console.log("Upload response from S3:", uploadResponse);
        if (!uploadResponse.ok) {
          console.error(`Error uploading ${file.name} to S3:`, uploadResponse.status, uploadResponse.statusText);
          alert(`Error uploading ${file.name} to S3.`);
          continue;
        }
        console.log(`Successfully uploaded ${file.name} to S3.`);

        // Store the uploaded video details
        uploadedVideos.push({
          video_id: videoId,
          video_name: file.name, // Original filename (or converted MP3 filename)
          video_url: presignedData.file_name, // S3 file path
        });

        // Set the uploaded video_id for the progress bar (if applicable)
        setUploadedVideoId(videoId);
      }

      // Step 3: Send the video details to your backend API for processing
      if (uploadedVideos.length > 0) {
        console.log("Sending video details to backend API:", uploadedVideos);

        const formData = new FormData();
        uploadedVideos.forEach((video) => {
          formData.append("video_files", video.video_name);
          formData.append("video_urls", video.video_url);
          formData.append("video_id", video.video_id);
        });
        formData.append("mode", Mode);
        formData.append("video_type", video_type);

        const response = await uploadVideo(formData); // Call your backend API
        console.log("Response from backend API:", response);

        if (response?.video_id) {
          // If the backend returns a video_id, start polling its status
          await pollStatus(response.video_id);
        } else {
          console.error("No video_id received from backend API response.");
          alert("Video uploaded, but failed to get processing ID from server.");
        }
      }

      setStatus("submit"); // Change status to "submit" to show processing animation
      setSelectedFiles([]); // Clear selected files after successful upload initiation
    } catch (error) {
      console.error("Overall upload process failed:", error);
      alert(`Error during upload: ${error.message}`);
    } finally {
      setIsUploading(false); // Reset uploading state
    }
  };

  /**
   * Polls the status of the video processing on the backend.
   * @param {string} videoId - The ID of the video to poll.
   */
  const pollStatus = async (videoId) => {
    let interval;
    try {
      interval = setInterval(async () => {
        const statusResponse = await getStatus(videoId); // Call your backend status API

        if (statusResponse && statusResponse.status === "completed") {
          clearInterval(interval); // Stop polling
          const score = await getComscore(videoId); // Get communication score
          setComscore(score);
          setStatus(statusResponse.status); // Set status to "completed"
          setCompletedVideoId(videoId);
          console.log("Video processing completed:", statusResponse);
        } else {
          console.log("Waiting for video to complete processing...");
        }
      }, 5000); // Poll every 5 seconds
    } catch (error) {
      console.error("Error polling status:", error);
      clearInterval(interval);
      alert("Error checking video processing status.");
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 z-50">
      <div className="bg-white p-4 sm:p-6 md:p-8 rounded-3xl shadow-lg w-full max-w-4xl aspect-[6/4] min-h-[25rem] md:min-h-[30rem] max-h-[80vh] flex flex-col justify-center relative">
        {/* Close Button */}
        {status === 'uploadvideo' && (
          <button
            onClick={onClose}
            className="absolute top-1 right-2 sm:top-4 sm:right-4 md:top-2 md:right-4 lg:top-6 lg:right-6 text-gray-700 hover:text-gray-900 text-lg sm:text-xl md:text-2xl"
          >
            ✕
          </button>
        )}

        {/* Upload Video/Audio Section */}
        {status === 'uploadvideo' && (
          <div className="border-dashed border-2 border-[#7AA5FB] p-4 sm:p-6 bg-blue-50 rounded-3xl w-full max-w-[90vw] sm:max-w-[780px] h-auto min-h-[300px] sm:min-h-[440px] mx-auto flex flex-col items-center justify-center text-center">
            <div className="text-[#275FDD] text-2xl sm:text-3xl md:text-4xl mb-4 mt-6" style={{ marginTop: '0px' }}>
              <svg width="76" height="82" viewBox="0 0 76 82" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M21.7146 73.2875V78.7161C21.7146 80.2144 22.9306 81.4304 24.4289 81.4304H51.5717C53.07 81.4304 54.286 80.2144 54.286 78.7161V73.2875C54.286 71.7892 53.07 70.5732 51.5717 70.5732C50.0735 70.5732 48.8575 71.7892 48.8575 73.2875V76.0018H27.1432V73.2875C27.1432 71.7892 25.9272 70.5732 24.4289 70.5732C22.9306 70.5732 21.7146 71.7892 21.7146 73.2875ZM35.286 52.6969V67.8589C35.286 69.3572 36.502 70.5732 38.0003 70.5732C39.4986 70.5732 40.7146 69.3572 40.7146 67.8589V52.6969L41.5099 53.4922C42.5685 54.5508 44.2893 54.5508 45.3479 53.4922C46.4065 52.4337 46.4065 50.7128 45.3479 49.6542L39.9193 44.2257C38.8607 43.1644 37.1399 43.1644 36.0813 44.2257L30.6527 49.6542C29.5942 50.7128 29.5942 52.4337 30.6527 53.4922C31.7113 54.5508 33.4322 54.5508 34.4907 53.4922L35.286 52.6969Z" fill="#275FDD" />
                <path fillRule="evenodd" clipRule="evenodd" d="M35.2863 52.6969V67.8589C35.2863 69.3572 36.5023 70.5732 38.0006 70.5732C39.4989 70.5732 40.7149 69.3572 40.7149 67.8589V52.6969L41.5102 53.4922C42.5688 54.5508 44.2896 54.5508 45.3482 53.4922C46.4068 52.4337 46.4068 50.7128 45.3482 49.6542L39.9196 44.2257C38.8611 43.1644 37.1402 43.1644 36.0816 44.2257L30.6531 49.6542C29.5945 50.7128 29.5945 52.4337 30.6531 53.4922C31.7116 54.5508 33.4325 54.5508 34.4911 53.4922L35.2863 52.6969Z" fill="#275FDD" />
                <path fillRule="evenodd" clipRule="evenodd" d="M24.8927 48.8571C25.2863 47.7443 25.9241 46.702 26.8144 45.8144L32.243 40.3859C35.4214 37.2047 40.5786 37.2047 43.757 40.3859L49.1856 45.8144C50.0759 46.702 50.7137 47.7443 51.1073 48.8571H62.0594C64.771 48.8571 67.2546 47.7904 69.1573 46.0071C73.2559 43.054 76 37.9973 76 32.2457C76 25.1831 71.8689 19.1873 66.1227 16.7743C64.6434 7.22 56.8724 0 47.6221 0C42.256 0 37.4001 2.41571 33.9693 6.31886C32.2321 5.60229 30.3457 5.21143 28.3779 5.21143C21.8446 5.21143 16.1989 9.53529 13.9134 15.7266C6.14786 16.5979 0 23.617 0 32.2457C0 38 2.74686 43.0594 6.86443 46.0261C8.75086 47.7931 11.2344 48.8571 13.9406 48.8571H24.8927ZM33.7847 13.8591C32.9433 13.4384 31.9444 13.4819 31.1437 13.9759C30.343 14.4726 29.8571 15.3439 29.8571 16.2857V32.5714C29.8571 33.5133 30.343 34.3846 31.1437 34.8813C31.9444 35.3753 32.9433 35.4187 33.7847 34.998L50.0704 26.8551C50.9906 26.3964 51.5714 25.4573 51.5714 24.4286C51.5714 23.3999 50.9906 22.4607 50.0704 22.002L33.7847 13.8591ZM35.2857 20.6774L42.788 24.4286L35.2857 28.1797V20.6774Z" fill="#275FDD" />
              </svg>
            </div>
            <p className="text-xs sm:text-base text-[#303972] text-center mb-4 font-normal " style={{ fontFamily: "sans-serif, Archivo" }}>
              <button
                onClick={() => document.getElementById("fileInput").click()}
                className="text-blue-500 text-base sm:text-base mb-2 hover:text-blue-700"
                disabled={isUploading || !ffmpegLoaded} // Disable if uploading or FFmpeg not loaded
              >
                Click here
              </button>
              <span> to upload video/audio file to get your</span>
              <br />
              <span>communication skills analysis report</span>
            </p>

            {/* File Input */}
            <input
              type="file"
              id="fileInput"
              // Dynamically set accept attribute based on mode
              // For audio, allow common audio types, as we'll convert to MP3 internally
              accept={Mode === 'audio' ? 'audio/mpeg,audio/wav,audio/ogg' : 'video/mp4'}
              className="hidden"
              onChange={handleFileSelect}
              disabled={isUploading || !ffmpegLoaded} // Disable if uploading or FFmpeg not loaded
            />

            {/* Gradient Underline */}
            <div className="w-2/3 sm:w-1/2 my-4 sm:my-6" style={{ height: "1px", background: "linear-gradient(to right, rgba(16, 69, 186, 0), #1045BA, rgba(16, 69, 186, 0))" }}></div>

            {selectedFiles.length > 0 && (
              <div className="w-full flex flex-wrap justify-center gap-2 sm:gap-4 ">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex flex-col items-center">
                    {file.type.startsWith('video/') ? (
                      <video src={URL.createObjectURL(file)} className="w-34 h-10 sm:w-32 sm:h-14 rounded-xl mb-2 object-cover" controls />
                    ) : (
                      <audio src={URL.createObjectURL(file)} controls className="w-34 h-10 sm:w-32 sm:h-14 rounded-xl mb-2" />
                    )}
                    <button className="text-black-500 hover:text-red-700" onClick={() => handleDeleteFile(index)} disabled={isUploading}>
                      <GoTrash className="text-2xl" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="w-full flex justify-end mr-5 mt-8 ">
              <button
                className={`px-3 sm:px-4 py-2 rounded-xl flex items-center text-sm sm:text-base ${selectedFiles.length === 0 || isUploading || !ffmpegLoaded
                  ? "bg-[#2563EB80] cursor-not-allowed text-white"
                  : "bg-[#2563EB] text-white"
                  }`}
                onClick={handleUpload}
                style={{ fontFamily: "sans-serif, Inter" }}
                disabled={selectedFiles.length === 0 || isUploading || !ffmpegLoaded} // Disable if no files, uploading, or FFmpeg not loaded
              >
                <FiUpload className="me-2 w-5 h-5 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                {isUploading
                  ? `Uploading ${mode === 'audio' ? 'audio' : 'video'}...`
                  : `Upload ${mode === 'audio' ? 'audio' : 'video'}`
                }
              </button>
            </div>
          </div>
        )}

        {/* Processing/Submit Section */}
        {status === 'submit' && (
          <div className="w-full h-full flex flex-col items-center mt-28 justify-middle">
            <Processesimg />
            <p className="w-4/5 sm:w-3/5 text-center text-[#ABABAB] mt-4 sm:mt-5 text-xs sm:text-sm md:text-base"
              style={{ fontFamily: "sans-serif, Archivo" }}
            >
              Sit back and relax while we process your {Mode === 'audio' ? 'audio' : 'video'}! We'll let you know once it's ready.
            </p>
            {isUploading && !uploadedVideoId && (
              <p className="text-sm text-gray-600 mt-10">Uploading {mode === 'audio' ? 'audio' : 'video'}...</p>
            )}
            {uploadedVideoId && (mode !== 'audio' ? (
              <ProgressBar videoId={uploadedVideoId} />
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
                <RingLoader color="#123abc" loading={true} size={60} /> 
                <p style={{ marginLeft: '10px' }}>Uploading audio...</p> 
              </div>
            ))}
          </div>
        )}

        {/* Completed Popup Section */}
        {status === 'completed' && showCompletedPop && (
          <Completedpop
            Mode={mode}
            videoId={completedVideoId}
            comscore={comscore}
            onClose={() => {
              setShowCompletedPop(false); // Close the Completedpop
              onClose && onClose(); // Close the UploadPopup if the function exists
            }}
          />
        )}
      </div>
    </div>
  );
};

export default UploadPopup;